import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Grid, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from '../../components/toast';
import { useLoadingState } from '../../hooks/useLoadingState';
import DeleteConfirmationDialog from '../../components/common/DeleteConfirmationDialog';
import {
  MoneyAccountStatsCards,
  MoneyAccountFilters,
  MoneyAccountTable,
  MoneyAccountForm,
} from '../../components/moneyAccount';
import { ExpenseForm } from '../../components/expense';
import { IncomeForm } from '../../components/income';
import { moneyAccountValidationSchema } from '../../utils/moneyAccountValidation';
import { expenseValidationSchema } from '../../utils/expenseValidation';
import { incomeValidationSchema } from '../../utils/incomeValidation';
import {
  getInitialMoneyAccountFormData,
  getMoneyAccountFormDataFromRecord,
  formatMoneyAccountDataForSubmission,
} from '../../utils/moneyAccountUtils';
import { getInitialExpenseFormData, formatExpenseDataForSubmission } from '../../utils/expenseUtils';
import { getInitialIncomeFormData, formatIncomeDataForSubmission } from '../../utils/incomeUtils';
import {
  getMoneyAccounts,
  getMoneyAccountDetails,
  createMoneyAccount,
  updateMoneyAccount,
  deleteMoneyAccount,
  createExpense,
  createIncome,
} from '../../services/api';
import { getUserGroups } from '../../utils';
import { HRPageHeader } from '../../components/hr';
import { LightTooltip } from '../../components/custom-components/custom-tooltip';

const allowedGroups = ['admin', 'hr'];

export default function MoneyAccountManagementPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [dialogMode, setDialogMode] = useState('add');
  const [searchTerm, setSearchTerm] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('All');
  const [currencyFilter, setCurrencyFilter] = useState('All');
  const [stats, setStats] = useState({ total_accounts: 0, total_balance: 0, active_accounts: 0 });
  const [filterOptions, setFilterOptions] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Quick action dialogs
  const [openExpenseDialog, setOpenExpenseDialog] = useState(false);
  const [openIncomeDialog, setOpenIncomeDialog] = useState(false);
  const [expenseFormData, setExpenseFormData] = useState(getInitialExpenseFormData());
  const [incomeFormData, setIncomeFormData] = useState(getInitialIncomeFormData());
  const [expenseFormErrors, setExpenseFormErrors] = useState({});
  const [incomeFormErrors, setIncomeFormErrors] = useState({});

  const [formData, setFormData] = useState(getInitialMoneyAccountFormData());
  const { isLoading, withLoading } = useLoadingState({ minLoadingTime: 800 });

  useEffect(() => {
    const userGroups = getUserGroups();
    if (userGroups === null || !allowedGroups.some((group) => userGroups.includes(group))) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const loadAccounts = useCallback(async () => {
    await withLoading(async () => {
      const res = await getMoneyAccounts(page + 1, rowsPerPage, searchTerm, accountTypeFilter, currencyFilter);
      const data = res.data || {};
      setAccounts(data.results || data.accounts || []);
      setTotalCount(data.count ?? (data.results ? data.results.length : data.accounts?.length || 0));
      if (data.stats) {
        setStats({
          total_accounts: data.stats.total_accounts || 0,
          total_balance: data.stats.total_balance || 0,
          active_accounts: data.stats.active_accounts || 0,
        });
      }
      // Update filter options if included in response
      if (data.filter_options) {
        setFilterOptions(data.filter_options);
      }
    });
  }, [page, rowsPerPage, searchTerm, accountTypeFilter, currencyFilter, withLoading]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = async (mode, account = null) => {
    setDialogMode(mode);
    setSelectedAccount(account);
    if ((mode === 'edit' || mode === 'view') && account) {
      try {
        const { data } = await getMoneyAccountDetails(account.id);
        const acc = data.account || data;
        setFormData(getMoneyAccountFormDataFromRecord(acc));
      } catch (_) {
        setFormData(getMoneyAccountFormDataFromRecord(account));
      }
    } else {
      setFormData(getInitialMoneyAccountFormData());
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAccount(null);
    setFormErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = async () => {
    try {
      await moneyAccountValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      setFormErrors(errors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (!isValid) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    try {
      const payload = formatMoneyAccountDataForSubmission(formData);
      if (dialogMode === 'add') {
        await createMoneyAccount(payload);
        toast.success('Money account created');
      } else {
        await updateMoneyAccount(selectedAccount.id, payload);
        toast.success('Money account updated');
      }
      handleCloseDialog();
      loadAccounts();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save money account');
    }
  };

  const handleDeleteAccount = (account) => {
    setAccountToDelete(account);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (accountToDelete) {
      try {
        await deleteMoneyAccount(accountToDelete.id);
        toast.success('Money account deleted successfully');
        setOpenDeleteDialog(false);
        setAccountToDelete(null);
        loadAccounts();
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Failed to delete money account');
      }
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setAccountToDelete(null);
  };

  // Quick action handlers
  const handleAddExpense = (account) => {
    const initialData = getInitialExpenseFormData();
    initialData.money_account = account.id.toString();
    setExpenseFormData(initialData);
    setSelectedAccount(account);
    setOpenExpenseDialog(true);
  };

  const handleAddIncome = (account) => {
    const initialData = getInitialIncomeFormData();
    initialData.money_account = account.id.toString();
    setIncomeFormData(initialData);
    setSelectedAccount(account);
    setOpenIncomeDialog(true);
  };

  const handleExpenseInputChange = (field, value) => {
    setExpenseFormData((prev) => ({ ...prev, [field]: value }));
    if (expenseFormErrors[field]) {
      setExpenseFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleIncomeInputChange = (field, value) => {
    setIncomeFormData((prev) => ({ ...prev, [field]: value }));
    if (incomeFormErrors[field]) {
      setIncomeFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateExpenseForm = async () => {
    try {
      await expenseValidationSchema.validate(expenseFormData, { abortEarly: false });
      setExpenseFormErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      setExpenseFormErrors(errors);
      return false;
    }
  };

  const validateIncomeForm = async () => {
    try {
      await incomeValidationSchema.validate(incomeFormData, { abortEarly: false });
      setIncomeFormErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      setIncomeFormErrors(errors);
      return false;
    }
  };

  const handleExpenseSubmit = async () => {
    const isValid = await validateExpenseForm();
    if (!isValid) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    try {
      const payload = formatExpenseDataForSubmission(expenseFormData);
      await createExpense(payload);
      toast.success('Expense created');
      setOpenExpenseDialog(false);
      setExpenseFormData(getInitialExpenseFormData());
      setExpenseFormErrors({});
      loadAccounts(); // Refresh to show updated balance
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create expense');
    }
  };

  const handleIncomeSubmit = async () => {
    const isValid = await validateIncomeForm();
    if (!isValid) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    try {
      const payload = formatIncomeDataForSubmission(incomeFormData);
      await createIncome(payload);
      toast.success('Income created');
      setOpenIncomeDialog(false);
      setIncomeFormData(getInitialIncomeFormData());
      setIncomeFormErrors({});
      loadAccounts(); // Refresh to show updated balance
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create income');
    }
  };

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Money Account Management</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HRPageHeader
            title="Money account management"
            description="Maintain bank and cash accounts used for payroll, expenses, and income. Balances here feed other HR finance screens."
            actions={
              <LightTooltip title="Register a new bank or cash account for HR transactions.">
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('add')}>
                  Add account
                </Button>
              </LightTooltip>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <MoneyAccountStatsCards stats={stats} isLoading={isLoading} />
        </Grid>

        <MoneyAccountFilters
          searchTerm={searchTerm}
          accountTypeFilter={accountTypeFilter}
          currencyFilter={currencyFilter}
          filterOptions={filterOptions}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setPage(0);
          }}
          onAccountTypeFilterChange={(value) => {
            setAccountTypeFilter(value);
            setPage(0);
          }}
          onCurrencyFilterChange={(value) => {
            setCurrencyFilter(value);
            setPage(0);
          }}
          onClearFilters={() => {
            setSearchTerm('');
            setAccountTypeFilter('All');
            setCurrencyFilter('All');
            setPage(0);
          }}
        />

        <MoneyAccountTable
          accounts={accounts}
          isLoading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onView={(account) => handleOpenDialog('view', account)}
          onEdit={(account) => handleOpenDialog('edit', account)}
          onDelete={handleDeleteAccount}
          onAddIncome={handleAddIncome}
          onAddExpense={handleAddExpense}
        />
      </Grid>

      <MoneyAccountForm
        open={openDialog}
        mode={dialogMode}
        formData={formData}
        formErrors={formErrors}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
      />

      <ExpenseForm
        open={openExpenseDialog}
        mode="add"
        formData={expenseFormData}
        formErrors={expenseFormErrors}
        moneyAccounts={[selectedAccount].filter(Boolean)}
        onClose={() => {
          setOpenExpenseDialog(false);
          setExpenseFormData(getInitialExpenseFormData());
          setExpenseFormErrors({});
        }}
        onSubmit={handleExpenseSubmit}
        onInputChange={handleExpenseInputChange}
      />

      <IncomeForm
        open={openIncomeDialog}
        mode="add"
        formData={incomeFormData}
        formErrors={incomeFormErrors}
        moneyAccounts={[selectedAccount].filter(Boolean)}
        onClose={() => {
          setOpenIncomeDialog(false);
          setIncomeFormData(getInitialIncomeFormData());
          setIncomeFormErrors({});
        }}
        onSubmit={handleIncomeSubmit}
        onInputChange={handleIncomeInputChange}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        title="Confirm Delete"
        message="Are you sure you want to delete the money account"
        itemName={accountToDelete?.name}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmButtonText="Delete Account"
      />
    </>
  );
}
